import { useEffect, useState, useRef } from 'react';
import { flushSync } from "react-dom";
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import LoadingButton from "./LoadingButton";
import {
    isPhoneNumberValid,
    parsePhoneNumber,
    isEmailValid,
    getFileExtension,
    checkObjectsEqual,
    checkWhiteSpace,
    checkValidPositiveInteger,
    checkValuesEqual,
    checkValid,
    getFormattedValue,
    readFileAsTextAndSanitize,
    isStartWithVowel, formatPhoneNumberWithDashes, removeHyphens, hasCharacterRepeatedRegex,
    getTextFromCopyGroup,
} from '../../utils/utils';
import { BsFillFilePdfFill } from "react-icons/bs";
import { PiFileDocFill, PiFileHtmlFill, PiFilePptFill, PiFileXlsFill } from 'react-icons/pi';
import { FaImage } from "react-icons/fa";
import { ALIGN, MIME_TYPES, POSITIONS, VALUE } from "../../constants";
import styles from './Form.module.scss';
import PdfViewer from "../ui/PdfViewer";
import messages from '../../utils/helper/messages';
import Icon from './Icon';
import SystemFileModal from './SystemFileModal';
import CopyToClipboardIcon from './CopyToClipboardIcon';
import CharacterCounter from './CharacterCounter';

function SharedForm({
    array,
    modelObj,
    onSubmit,
    onCancel,
    onInputChanged,
    cols = 4,
    useStrictPhoneValidation = true,
    loading,
    disabled = false,
    actionBarPosition = POSITIONS.BOTTOM,
    actionBarAlign = ALIGN.LEFT,
    actionBarStyle = '',
    submitButtonText = 'Submit',
    cancelButtonText = 'Cancel',
    viewOnly = false,
    formStyle = null,
    showCancel = true,
    cloneInitial = false,
    onFormValidate = null,
}) {
    const [validated, setValidated] = useState(false);
    const [imgName, setImgName] = useState({});
    const [imgSrc, setImgSrc] = useState({});
    const [formData, setFormData] = useState(null);
    const [errors, setErrors] = useState({});
    const [formFieldsValidated, setFormFieldsValidated] = useState({});
    const [isSubmitDisable, setIsSubmitDisable] = useState(disabled);
    const [selectedSystemFile, setSelectedSystemFile] = useState('');
    const [initialData, setInitialData] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const defaultFileTypes = [MIME_TYPES.PNG, MIME_TYPES.JPG, MIME_TYPES.GIF];
    const formRef = useRef(null);
    const position = useRef({ beforeStart: 0, beforeEnd: 0 });
    const inputRef = useRef(null);
    const cancelClickFromMouseDownRef = useRef(false);

    let keyAction = 'add';

    useEffect(() => {
        setFormDataIfNeeded();
    }, [modelObj]);

    useEffect(() => {
        setFormFieldsValidated(validateAllRequiredFieldsPresent());
    }, [formData]);

    useEffect(() => {
        removeErrorOnOptionsDelayedLoad();
    }, [array]);

    useEffect(() => {
        if (!formData || !errors) return;

        let updated = false;
        const newErrors = { ...errors };
        // If the field previously had a required error, and now if it’s optional and empty, delete the error.
        array.forEach((field) => {
            if (
                newErrors[field.key] &&
                !isRequired(field) &&
                [undefined, null, ''].includes(formData[field.key])
            ) {
                delete newErrors[field.key];
                updated = true;
            }
        });

        if (updated) {
            setErrors(newErrors);
        }
    }, [formData, array]);

    const removeErrorOnOptionsDelayedLoad = () => {
        for (let item of array) {
            if (item.type === 'options' && item.optionValues && item.optionValues.length > 0) {
                delete errors[item.key];
                setErrors({ ...errors });
            }
        }
    }

    const setFormDataIfNeeded = () => {
        if (!modelObj) { // Form action - ADD
            let initialFormData = {};
            for (let item of array) {
                // if option, select the first item in the array as the default value
                if (item.type === 'options') {
                    initialFormData[item.key] = item.optionValues && item.optionValues[0]?.id;
                } else {
                    initialFormData[item.key] = '';
                }
            }

            setFormData(initialFormData);
        } else { // Form action - EDIT
            let initialFormData = {
                ...modelObj
            }

            const phoneFields = array.filter(item => item.type === 'tel');
            if (cloneInitial && !isDirty) {
                let clonedInitialData = { ...initialFormData };
                if (phoneFields.length > 0) {
                    for (let item of phoneFields) {
                        clonedInitialData[item.key] = removeHyphens(initialFormData[item.key]);
                    }
                }
                setInitialData(clonedInitialData);
            }
            if (phoneFields.length > 0) {
                for (let item of phoneFields) {
                    initialFormData[item.key] = formatPhoneNumberWithDashes(modelObj[item.key]);
                }
            }
            setFormData(initialFormData);

            if (!cloneInitial && !isDirty) {
                setIsSubmitDisable(true);
            }

            for (let item of array) {
                if (item.type === 'file') {
                    if (!imgName[item.key] && modelObj['fileURL']) {
                        if (modelObj['fileURL'].imageSrc) {
                            setImgSrc((prevState) => ({ ...prevState, [item.key]: `${modelObj['fileURL'].imageSrc}` }));
                        } else {
                            setImgSrc((prevState) => ({ ...prevState, [item.key]: `${modelObj['fileURL']}` }));
                        }
                    }
                    const imageName = imgName[item.fileNameKey] ?? imgName[item.key];
                    if (!imageName && modelObj[item.fileURLKey]) {
                        setImgName((prevState) => ({ ...prevState, [item.fileNameKey]: modelObj[item.fileNameKey] }));
                        setImgSrc((prevState) => ({ ...prevState, [item.key]: modelObj[item.fileURLKey] }));
                    }
                }
                if (item.type === 'options' && (!item.optionValues || item.optionValues.length === 0)) {
                    setErrors((prevErrors) => ({ ...prevErrors, [item.key]: { key: true, value: "" } }));
                }
            }
        }
    };

    const validateValue = (valid, type, key, required, maxValue, labelName, customError,  value = undefined) => {
        const inputValue = value === undefined ? formData[key] : value;
        if (required === true && [undefined, null, ''].includes(inputValue)) {
            setErrors({ ...errors, [key]: { key: true, value: customError ?? `${labelName} is required` } });
            return false;
        }

        if (valid !== undefined && !['tel', 'email'].includes(type)) {
            if (!required && [undefined, null, ''].includes(inputValue)) {
                return;
            }
            const value = type === 'number' ? Number(inputValue) : inputValue;
            const validityCheck = checkValid(value, valid);
            if (!validityCheck.valid) {
                setErrors({
                    ...errors,
                    [key]: { key: true, value: validityCheck.error }
                });
                return false;
            }
            if (type === 'textarea' && key in formData && checkWhiteSpace(inputValue) && ![undefined, null, ''].includes(inputValue)) {
                setErrors({ ...errors, [key]: { key: true, value: "Text cannot have a leading or trailing white space or multiple spaces." } });
                return false;
            }

        } else {
            if (['tel', 'email'].includes(type) && ((required === false && inputValue?.length > 0) || required !== false)) {
                if (type === 'tel' && key in formData && !isPhoneNumberValid(inputValue, useStrictPhoneValidation)) {
                    setErrors({ ...errors, [key]: { key: true, value: "It looks like the number entered isn't registered as a valid phone number." } });
                    return false;
                }
                if (type === 'email' && key in formData && !isEmailValid(inputValue)) {
                    setErrors({ ...errors, [key]: { key: true, value: "Please enter a valid email address" } });
                    return false;
                }
            }
            else if (['number', 'text', 'textarea'].includes(type) && ![undefined, null, ''].includes(inputValue)) {
                if ((type === 'text' || type === 'textarea') && key in formData && checkWhiteSpace(inputValue)) {
                    setErrors({ ...errors, [key]: { key: true, value: "Text cannot have a leading or trailing white space or multiple spaces." } });
                    return false;
                }
                if (type === 'number' && key in formData) {
                    if (!(checkValidPositiveInteger(inputValue))) {
                        if (maxValue && inputValue < maxValue) {
                            setErrors({ ...errors, [key]: { key: true, value: `Number must be a positive integer value less than ${maxValue}.` } });
                            return false;
                        } else {
                            setErrors({ ...errors, [key]: { key: true, value: `Number must be a positive integer.` } });
                            return false;
                        }
                    }
                }
            }
        }

        // if none of the validations trigger, remove the error for the key
        const updatedErrors = { ...errors };
        if (updatedErrors[key]) {
            delete updatedErrors[key];
        }
        setErrors(updatedErrors);
    }

    const validateFormData = () => {
        for (let item of array) {
            validateValue(item.valid, item.type, item.key, isRequired(item), item.maxValue, item.labelName, item.customError);
            if (errors[item.key] && errors[item.key].key) {
                return false;
            }
        }

        return true;
    }

    const transformFormDataIfNeeded = (formData) => {
        for (let item of array) {
            if ((['tel'].includes(item.type) && item.required === false && formData[item.key]?.length > 0) || item.required === undefined) {
                if (item.type === 'tel' && item.key in formData) {
                    formData[item.key] = parsePhoneNumber(formData[item.key]);
                }
            }
        }
    };

    const FileName = ({ icon, name, item }) => {
        const Icon = icon;
        return (
            <div className={`${styles.fileWrap} ${(!imgName[item.key] && name) && styles.withImage}`} title={name}>
                <span className={styles.iconWrap}>
                    <Icon className="text-muted" />
                </span>
                {(!imgName[item.key] && name) &&
                    <span className={`${styles.fileName} flex-grow-1 text-truncate`}>
                        {name}
                    </span>
                }
            </div>
        );
    };

    const FileTypeDisplay = ({ item }) => {
        const fileNameKey = item.fileNameKey ?? item.key;
        const fileName = formData[fileNameKey] && typeof formData[fileNameKey] === "object" ? formData[fileNameKey].name : formData[fileNameKey];
        const fileExt = getFileExtension(fileName);

        if (item.type === 'systemFile') {
            return (<FileName icon={PiFileXlsFill} item={item} />)
        }

        const handleRemoveFile = () => {
            formData[fileNameKey] = null;
            imgSrc[item.key] = null;
            setFormData({ ...formData });
            setIsSubmitDisable(false);
        };

        const RemoveButton = () => (
            <Button
                variant="light"
                className={`position-absolute end-0 top-0 ${styles.removeFileButton}`}
                onClick={handleRemoveFile}
            >
                <Icon icon="remove" size={12} />
            </Button>
        );

        switch (fileExt) {
            case 'pdf':
                return (
                    <>
                        <FileName name={fileName} icon={BsFillFilePdfFill} item={item} />
                        {item.required === false && <RemoveButton />}
                        <PdfViewer file={imgSrc[item.key]} />
                    </>
                );
            case 'html':
                return (
                    <>
                        <FileName name={fileName} icon={PiFileHtmlFill} item={item} />
                        {item.required === false && <RemoveButton />}
                        <object
                            data={imgSrc[item.key]}
                            type={MIME_TYPES.HTML}
                            className="w-100 border border-1 rounded mt-2"
                        />
                    </>
                );
            case 'jpg':
            case 'jfif':
            case 'png':
            case 'gif':
            case 'svg':
                return (
                    <>
                        <FileName name={fileName} icon={FaImage} item={item} />
                        {item.required === false && <RemoveButton />}
                        <div className={`${styles.imageWrap} w-100 border border-1 bg-light rounded-top mt-2`}>
                            <img src={imgSrc[item.key]} alt={formData[item.key]} />
                        </div>
                    </>
                );
            case 'xlsx':
            case 'csv':
                return (
                    <>
                        <FileName name={fileName} icon={PiFileXlsFill} item={item} />
                        {item.required === false && <RemoveButton />}
                    </>
                )
            case 'docx':
                return (
                    <>
                        <FileName name={fileName} icon={PiFileDocFill} item={item} />
                        {item.required === false && <RemoveButton />}
                    </>
                )
            case 'pptx':
                return (
                    <>
                        <FileName name={fileName} icon={PiFilePptFill} item={item} />
                        {item.required === false && <RemoveButton />}
                    </>
                )
            default:
                return null;
        }
    }

    const scrollToInvalidInput = () => {
        const form = formRef.current;
        const invalidInputs = Array.from(form.querySelectorAll(':invalid, .is-invalid'));
        invalidInputs.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
        invalidInputs[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    const handleSubmit = (event) => {
        const form = event.currentTarget;

        event.preventDefault();
        event.stopPropagation();

        if (form.checkValidity() === false) {
            setValidated(true);
            setTimeout(scrollToInvalidInput, 10);
        } else {
            const formSubmitData = new FormData(event.currentTarget);
            const formSubmitDataObj = Object.fromEntries(formSubmitData.entries());

            if (!validateFormData()) {
                setValidated(false);
                setTimeout(scrollToInvalidInput, 10);
                return;
            }

            transformFormDataIfNeeded(formSubmitDataObj);

            if (onSubmit) {
                onSubmit(formSubmitDataObj);
            }
        }
    };

    const inputChanged = async (e) => {
        let { name, value, type, checked } = e.target;
        let inputValue = value;

        const beforeStart = e.target.selectionStart;
        const beforeEnd = e.target.selectionEnd;
        const field = array.find(f => f.key === name);

        setIsDirty(true);

        if (e.target.files) {
            const selectedFile = e.target.files[0];
            let sanitizedFile; // initialize
            let allowedTypes = e.target.accept.split(',');
            setImgName((prevState) => ({ ...prevState, [name]: value }));
            if (allowedTypes.some((type) => type === selectedFile.type)) {
                const MIN_FILE_SIZE = 1 // 1KB
                const MAX_FILE_SIZE = 10240 // 10MB
                const fileSizeKiloBytes = selectedFile.size / 1024
                if (fileSizeKiloBytes < MIN_FILE_SIZE) {
                    setErrors({ ...errors, [e.target.name]: { key: true, value: "File size is less than minimum limit" } });
                    setValidated(false);
                    return
                }
                if (fileSizeKiloBytes > MAX_FILE_SIZE) {
                    setErrors({ ...errors, [e.target.name]: { key: true, value: "File size is greater than maximum limit" } });
                    setValidated(false);
                    return
                }
                if (e.target.maxLength > 0 && e.target.maxLength < selectedFile.name.length) {
                    setErrors({ ...errors, [e.target.name]: { key: true, value: `File name should contains ${e.target.maxLength} charecters or less` } });
                    setValidated(false);
                    return;
                }
                // Check if the file is a .jfif
                if (selectedFile.name.toLowerCase().endsWith('.jfif')) {
                    setErrors({ ...errors, [e.target.name]: { key: true, value: messages.info.checkJfifFile } });
                    setValidated(false);
                    return;
                }
                if (selectedFile && selectedFile.type === MIME_TYPES.HTML) {
                    try {
                        const sanitizedContent = await readFileAsTextAndSanitize(selectedFile);
                        const blob = new Blob([sanitizedContent], { type: MIME_TYPES.HTML });
                        sanitizedFile = new File([blob], selectedFile.name, { type: MIME_TYPES.HTML });
                    } catch (error) {
                        setErrors({ ...errors, [e.target.name]: { key: true, value: `Error reading or sanitizing file: ${error}` } });
                        setValidated(false);
                    }
                }
            }
            else {
                setErrors({ ...errors, [e.target.name]: { key: true, value: `This format is not allowed, please upload allowed file types only` } });
                setValidated(false);
                return;
            }

            let file = selectedFile.type === MIME_TYPES.HTML ? sanitizedFile : selectedFile;
            let previewSrc = URL.createObjectURL(file);
            if (onInputChanged) {
                onInputChanged(name, file);
            }
            setFormData({ ...formData, [name]: file });
            setImgSrc((prevState) => ({ ...prevState, [name]: previewSrc }));
        } else {
            let checkValues = [];
            if (type === 'checkbox') {
                let checkBoxes = document.querySelectorAll(`[name=${name}]`);
                for (let checkBox of checkBoxes) {
                    if (checkBox.checked) {
                        checkValues.push(checkBox.value);
                    }
                }
                if (!checked) value = "";
            }

            if (type === 'checkbox') {
                inputValue = checkValues;
            } else if (type === 'tel') {
                value = e.currentTarget.value;
                inputValue = formatPhoneNumberWithDashes(value);
                position.current = {
                    beforeStart,
                    beforeEnd
                };
            } else {
                inputValue = value !== "" ? value : null;
            }

            if (onInputChanged) {
                onInputChanged(name, inputValue);
            }

            // force state update immediately
            flushSync(() => {
                setFormData((data) => ({ ...data, [name]: inputValue }));
            });
            if (field.type === 'tel') {
                // move back the cursor at its editing place if digit position is less than 9 (xxx-xxx-)
                const valueHasDashes = hasCharacterRepeatedRegex(inputValue, '-');
                if (beforeStart < 9 && valueHasDashes) {
                    // update position for digits after dashes are added at position 4 and 8
                    if (beforeStart === 4 || beforeStart === 8) {
                        let updatedPosStart = beforeStart;
                        let updatedPosEnd = beforeEnd;
                        if (keyAction === 'add') {
                            updatedPosStart = beforeStart + 1;
                            updatedPosEnd = beforeEnd + 1;
                        } else if (keyAction === 'remove') {
                            updatedPosStart = beforeStart - 1;
                            updatedPosEnd = beforeEnd - 1;
                        }
                        position.current = {
                            updatedPosStart,
                            updatedPosEnd
                        };
                        e.target.setSelectionRange(updatedPosStart, updatedPosEnd);
                    } else {
                        e.target.setSelectionRange(beforeStart, beforeEnd);
                    }
                }
            }
        }

        if (field.resetFields) {
            handleFieldReset(name, field.resetFields);
        }
        const normalizedInputValue = getNormalizedValue(inputValue, field.type);
        const updatedFormData = {
            ...getNormalizedFormData(name),
            [name]: normalizedInputValue,
        };

        let updatedInitialData;
        if (cloneInitial) {
            updatedInitialData = {
                ...initialData,
                [name]: getNormalizedValue(initialData?.[name], field.type),
            }
        }

        const compareObj = cloneInitial ? updatedInitialData : modelObj;
        const disableFlag = checkObjectsEqual(compareObj, updatedFormData, name) && checkValuesEqual(compareObj[name], normalizedInputValue);
        setIsSubmitDisable(disableFlag);

        const isValid = validateValue(field.valid, field.type, field.key, isRequired(field), field.maxValue, field.labelName, field.customError, inputValue);

        // remove and update if needed
        const updatedErrors = { ...errors };
        delete updatedErrors[name];
        setErrors(updatedErrors);

        if (onFormValidate) {
            let cleanedErrors = { ...updatedErrors };
            if (field) {
                Object.keys(cleanedErrors).forEach(errKey => {
                    array.forEach(depField => {
                        if (depField.dependsOn === field.key && errKey === depField.key) {
                            delete cleanedErrors[errKey];
                        }
                    });
                });
            }
            const isFormValid = Object.keys(cleanedErrors).length === 0 && validateAllRequiredFieldsPresent({ key: name, value: inputValue });
            onFormValidate((isValid ?? true) && isFormValid, field.key);
        }
    };

    const getNormalizedFormData = (name) => {
        const fields = Object.keys(formData).filter(f => f !== name);
        const normalizedFormData = {};
        for (let field of fields) {
            const fieldType = array.find(f => f.key === field)?.type;
            normalizedFormData[field] = getNormalizedValue(formData[field], fieldType);
        }
        return normalizedFormData;
    }

    const getNormalizedValue = (value, type) => {
        let normalizedValue;
        if (type === 'tel') {
            normalizedValue = removeHyphens(value);
        } else if (type === 'number') {
            normalizedValue = parseFloat(value);
        } else {
            normalizedValue = value;
        }
        return normalizedValue;
    }

    const handleOnInput = (event) => {
        let previousValue = formData[event.target.name];
        const currentValue = getNormalizedValue(event.target.value);

        if (currentValue.length > previousValue?.length) {
            keyAction = 'add';
        } else if (currentValue.length < previousValue?.length) {
            keyAction = 'remove';
        } else {
            keyAction = 'replacement'
        }
    }

    const handleBlur = (e) => {
        const field = array.find(f => f.key === e.target.name);
        if (field) {
            const isValid = validateValue(field.valid, field.type, field.key, isRequired(field), field.maxValue, field.labelName, field.customError, formData[field.key]);
            if (!isValid) {
                setValidated(false);
                return;
            }
            setIsSubmitDisable(checkObjectsEqual(modelObj, formData, field.key));

            const updatedErrors = { ...errors };
            delete updatedErrors[field.key];
            setErrors(updatedErrors);

            if (onFormValidate) {
                const isFormValid = Object.keys(updatedErrors).length === 0;
                onFormValidate((isValid ?? true) && isFormValid, field.key);
            }
        }
        setFormFieldsValidated(validateAllRequiredFieldsPresent());
        setValidated(true);
    };

    const handleFieldReset = (name, fieldNames) => {
        const controls = Array.from(formRef.current.elements);
        fieldNames.forEach(fieldName => {
            const control = controls.find(control => control.name === fieldName);
            if (control.getAttribute('data-type') === 'systemFile') {
                setSelectedSystemFile('');
            } else if (control.type === 'file') {
                control.value = null;
                setImgSrc('');
                setImgName('');
            } else {
                control.value = null;
            }
        });
    }

    // default require is true for all.
    const isRequired = (item) => {
        return (!((item.type === "file" && imgSrc[item.key]) || (item.required === false)))
    }

    const validateAllRequiredFieldsPresent = (currentField = null) => {
        const form = formRef.current;
        if (form) {
            const formControls = form.elements;
            const requiredFormControls = Array.from(formControls).filter(
                (item) => (item.required !== false && item.hidden !== true && item.disabled !== true && !['button', 'fieldset'].includes(item.tagName.toLowerCase()))
            );
            const isFormValid = [];
            if (formData != null) {
                const updatedErrors = { ...errors };
                requiredFormControls.forEach(item => {
                    if (item.name && item.name !== '') {
                        let value = formData[item.name];
                        if (currentField && item.name === currentField?.key) {
                            value = currentField.value;
                            if (!['', null, undefined].includes(value)) {
                                delete updatedErrors[currentField?.key];
                            }
                        }
                        isFormValid.push(value !== null && value !== '' && value !== undefined);
                    }
                });

                delete updatedErrors.systemFile;
                delete updatedErrors.file;
                setErrors(updatedErrors);
                return isFormValid.every((value) => value === true);
            }
        }
        return false;
    };

    const handleFileSelect = (name, value) => {
        setSelectedSystemFile(value);
        if (onInputChanged) {
            onInputChanged(name, value);
        }
        delete errors[name];
        setErrors({ ...errors });
    };

    const renderFormControl = (item) => {
        let disabled = false;
        disabled = viewOnly ? true : item.disabled || disabled;

        if (item.type === 'options') {
            if (viewOnly === false) {
                disabled = item.disabled && item.conditional ? (formData[item.dependsOn] === "" || !item.dependsValue.includes(formData[item.dependsOn])) : item.disabled;
            }

            return (
                <>
                    <Form.Select
                        disabled={disabled}
                        visible={!item.hidden}
                        name={item.key}
                        value={formData[item.key] ?? ''}
                        onChange={inputChanged}
                        onBlur={handleBlur}
                        required={isRequired(item)}
                        isInvalid={
                            errors[item.key]?.key &&
                            errors[item.key].key === true
                        }>
                        {item?.optionValues?.map((optionInfo) => {
                            return (
                                <option
                                    key={optionInfo.id}
                                    value={optionInfo.id}>
                                    {optionInfo.value}
                                </option>
                            );
                        })}
                    </Form.Select>
                    {item.formText ? <Form.Text muted
                        className="d-block w-100">
                        {item.formText}
                    </Form.Text> : null}
                    <Form.Control.Feedback type="invalid">
                        {errors[item.key]?.value
                            ? errors[item.key].value
                            : `Please choose ${isStartWithVowel(item.labelName)} ${item.labelName}.`}
                    </Form.Control.Feedback>
                </>
            );
        } else if (item.type === 'radio' || item.type === 'checkbox') {
            if (viewOnly === false) {
                const conditionallyDisabled = item.disabled && item.conditional ? formData[item.dependsOn] === '' || !item.dependsValue.includes(formData[item.dependsOn]) : item.disabled;
                const conditionallyHidden = item.hidden && item.conditional ? formData[item.dependsOn] === '' || !item.dependsValue.includes(formData[item.dependsOn]) : item.hidden;

                disabled = conditionallyDisabled || conditionallyHidden;
            }
            const isChecked = (val) => {
                let checked;
                if (item.type === 'radio') {
                    checked = val === formData[item.key];
                }
                if (item.type === 'checkbox') {
                    checked = formData[item.key] ? formData[item.key].includes(val) : false
                }
                return checked;
            }

            return (
                <>
                    <div className={`${errors[item.key]?.key &&
                        errors[item.key].key === true ? 'is-invalid' : ''}`}>
                        {item?.optionValues?.map((option) => {
                            return (
                                <Form.Check
                                    inline
                                    type={item.type}
                                    name={item.key}
                                    key={option.id}
                                    label={
                                        <span className={disabled ? 'text-muted' : ''}>
                                            {option.label}
                                        </span>
                                        }
                                    id={`${item.key}-${option.id}`}
                                    required={isRequired(item)}
                                    disabled={disabled}
                                    visible={!item.hidden}
                                    value={option.value}
                                    onChange={inputChanged}
                                    onBlur={handleBlur}
                                    checked={isChecked(option.value)}
                                    isInvalid={
                                        errors[item.key]?.key &&
                                        errors[item.key].key === true
                                    }
                                />
                            );
                        })}
                    </div>

                    <Form.Control.Feedback type="invalid">
                        {errors[item.key]?.value
                            ? errors[item.key].value
                            : `Please choose a valid ${item.labelName}.`}
                    </Form.Control.Feedback>
                </>
            );
        } else if (item.type === 'systemFile') {
            return (
                <>
                    <SystemFileModal
                        item={item}
                        onFileSelect={handleFileSelect}
                        onBlur={handleBlur}
                        fileApi={item.fileApi}
                    />
                    <Form.Control
                        type="text"
                        data-type="systemFile"
                        placeholder={item.placeholder ?? "No file chosen"}
                        name={item.key}
                        required={isRequired(item)}
                        value={formData.systemFile?.name ?? ''}
                        readOnly={true}
                        onChange={inputChanged}
                        onBlur={handleBlur}
                        style={{ userSelect: "none", cursor: "pointer" }}
                        isInvalid={
                            errors[item.key]?.key &&
                            errors[item.key].key === true
                        }
                    />
                    {selectedSystemFile && <FileTypeDisplay item={{ ...item, fileNameKey: selectedSystemFile.name }} />}
                    <Form.Text
                        muted
                        className="d-block w-100">
                        {'Allowed file types: CSV'}
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                        {errors[item.key]?.value
                            ? errors[item.key].value
                            : `Please choose ${isStartWithVowel(item.labelName)} ${item.labelName}.`}
                    </Form.Control.Feedback>
                </>
            );
        } else if (item.type === 'file') {
            let allowedFileTypes =
                item.allowedTypes?.join(',') ?? defaultFileTypes.join(',');
            let extensionsArr = Object.keys(MIME_TYPES).filter((format) =>
                allowedFileTypes
                    .split(',')
                    .some((mime) => mime === MIME_TYPES[format])
            );
            extensionsArr = extensionsArr.filter(extension => extension !== 'JFIF'); // remove this logic if JFIF is allowed.
            let helperText = extensionsArr.join(', ');

            return (
                <>
                    <Form.Control
                        type={item.type}
                        accept={allowedFileTypes}
                        placeholder={item.placeholderName}
                        required={isRequired(item)}
                        disabled={disabled}
                        visible={!item.hidden}
                        // value={imgName}
                        onChange={inputChanged}
                        onBlur={handleBlur}
                        name={item.key}
                        maxLength={item.maxLength}
                        isInvalid={
                            errors[item.key]?.key &&
                            errors[item.key].key === true
                        }
                    />
                    {imgSrc[item.key] && <FileTypeDisplay item={item} />}
                    <Form.Text
                        muted
                        className="d-block w-100">
                        Allowed file types: {helperText}
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                        {errors[item.key]?.value
                            ? errors[item.key].value
                            : `Please choose ${isStartWithVowel(item.labelName)} ${item.labelName}.`}
                    </Form.Control.Feedback>
                </>
            );
        } else if (item.type === 'textarea') {
            const showCharCounter = item?.charCounter === true;

            return (
                <>
                    <Form.Control
                        className={item.copy ? 'pe-5' : ''}
                        as="textarea"
                        rows={3}
                        placeholder={item.placeholderName}
                        required={isRequired(item)}
                        value={formData[item.key] ?? ''}
                        maxLength={item.maxLength ? item.maxLength : null}
                        minLength={item.minLength ? item.minLength : null}
                        onChange={inputChanged}
                        onBlur={handleBlur}
                        disabled={disabled}
                        visible={!item.hidden}
                        name={item.key}
                        isInvalid={
                            errors[item.key]?.key &&
                            errors[item.key].key === true
                        }
                    />
                    {item.copy && <CopyToClipboardIcon textToCopy={formData[item.key] ?? ''} tooltip="Copy address to clipboard" />}
                    {showCharCounter && (
                        <CharacterCounter
                            currentValue={formData[item.key]}
                            maxLength={item?.maxLength}
                        />
                    )}
                    {item.formText ? <Form.Text muted
                        className="d-block w-100">
                        {item.formText}
                    </Form.Text> : null}
                    <Form.Control.Feedback type="invalid">
                        {errors[item.key]?.value
                            ? errors[item.key].value
                            : `Please choose ${isStartWithVowel(item.labelName)} ${item.labelName}.`}
                    </Form.Control.Feedback>
                </>
            )
        } else {
            if (viewOnly === false) {
                disabled = item.disabled && item.conditional ? (formData[item.dependsOn] === "" || !item.dependsValue.includes(formData[item.dependsOn])) : item.disabled;
            }

            let inputValue = getFormattedValue(formData[item.key], item.type);
            let minValue = item.minValue;
            let maxValue = item.maxValue;
            if (item.type === 'date') {
                minValue = item.minDepends ? formData[item.minDepends] : item.minValue;
                maxValue = item.maxDepends ? formData[item.maxDepends] : item.maxValue;
            }
            const copyText = item.copyGroup ? getTextFromCopyGroup(item.copyGroup, formData) : formData[item.key];

            return (
                <>
                    <Form.Control
                        ref={inputRef}
                        type={item.type}
                        placeholder={item.placeholderName}
                        required={isRequired(item)}
                        value={inputValue}
                        maxLength={item.maxLength ? item.maxLength : null}
                        minLength={item.minLength ? item.minLength : null}
                        max={maxValue ? getFormattedValue(maxValue, item.type, VALUE.Max) : null}
                        min={minValue ? getFormattedValue(minValue, item.type, VALUE.Min) : null}
                        onChange={inputChanged}
                        onBlur={handleBlur}
                        onInput={item.type === 'tel' ? handleOnInput : null}
                        disabled={disabled}
                        visible={!item.hidden}
                        name={item.key}
                        isInvalid={
                            errors[item.key]?.key &&
                            errors[item.key].key === true
                        }
                    />
                    {item.copyGroup && <CopyToClipboardIcon
                        textToCopy={copyText}
                        onCopy={item.copyAction}
                        tooltip="Copy to clipboard"
                    />}
                    {item?.charCounter && (
                        <CharacterCounter
                            currentValue={formData[item.key]}
                            maxLength={item?.maxLength}
                        />
                    )}
                    {item.formText ? <Form.Text muted
                        className="d-block w-100">
                        {item.formText}
                    </Form.Text> : null}
                    <Form.Control.Feedback type="invalid">
                        {errors[item.key]?.value
                            ? errors[item.key].value
                            : `Please choose a valid ${item.labelName}.`}
                    </Form.Control.Feedback>
                </>
            );
        }
    };

    const FormActionsBar = () => {
        const form = formRef.current;
        const handleMouseDown = (e) => {
            if (e.button !== 0) return; // left click only
            cancelClickFromMouseDownRef.current = true;
            const active = document.activeElement;
            if (active && active instanceof HTMLElement && active !== e.currentTarget) {
                active.blur();
                setTimeout(() => {
                    if (e.target.name === "save") {
                       // Use requestSubmit to honor HTML5 validation
                       if (typeof form.requestSubmit === 'function') {
                           form.requestSubmit();
                       } else {
                           // Fallback for older browsers
                           form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                       }
                   } else if (e.target.name === "cancel") {
                       onCancel?.(e);
                       cancelClickFromMouseDownRef.current = false;
                   }
                }, 0);
            }
        };

        const handleCancelClick = (e) => {
           // If we already handled via mousedown, skip the click to avoid double-call
           if (cancelClickFromMouseDownRef.current) {
               e.preventDefault();
               return;
           }
           onCancel?.(e);
       };

        return (
        <div className={`text-${actionBarAlign} ${actionBarPosition === POSITIONS.BOTTOM ? 'mt-4' : 'mb-4'} ${actionBarStyle}`}>
            {showCancel && <Button variant="outline-primary" name="cancel" type="button" className="me-2" onMouseDown={handleMouseDown} onClick={handleCancelClick}>{cancelButtonText}</Button>}
            <fieldset className="d-inline-flex" disabled={loading || disabled || isSubmitDisable || Object.keys(errors).length > 0 || !formFieldsValidated}>
                <LoadingButton name="save" type="submit" loading={loading} onMouseDown={handleMouseDown}>{submitButtonText}</LoadingButton>
            </fieldset>
        </div>
        );
    }

    // wait for form data before rendering to be set to prevent warning
    if (!formData) {
        return null;
    }

    return (
        <Form noValidate validated={validated} onSubmit={handleSubmit} ref={formRef}>
            {
                !viewOnly && actionBarPosition === POSITIONS.TOP &&
                <FormActionsBar />
            }
            <Row className={`${formStyle} gx-3`}>
                {array.map((obj) => {
                    return (
                        <Form.Group as={Col} md={obj.cols ?? cols} controlId={`validationCustom${obj.key}`} className={`${obj.removeSpacing ? 'mb-1' : 'mb-3'} ${obj.required !== false ? ' required' : ''}`} key={obj.key}
                            style={{
                                display: obj.hidden || obj.hidden && obj.conditional && (formData[obj.dependsOn] === "" || obj.dependsValue.includes(formData[obj.dependsOn])) ? 'none' : null
                            }}
                        >
                            <Form.Label className={obj.labelName === '' && 'd-none'}>{obj.labelName}</Form.Label>
                            <InputGroup hasValidation>
                                {renderFormControl(obj)}
                            </InputGroup>
                        </Form.Group>
                    )
                })
                }
            </Row>
            {
                actionBarPosition === POSITIONS.BOTTOM &&
                <FormActionsBar />
            }
        </Form>
    );
}

export default SharedForm;
